import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';
import { PageContainer, DataTableContainer } from '../../../components';
import { useAuth, useBonusRevenueTypes, useCiroAktar } from '../../../hooks';
import { useNotification } from '../../../hooks/useNotification';
import { TABLE_COLUMNS, FILTER_CONFIGS, SEARCH_CONFIGS } from '../../../constants/tableConfigurations';
import { formatDateTR } from '../../../utils/dateUtils';

// Yetki: ekip lideri (TEAM_MANAGER) sadece kendi ekibinin cirolarini aktarabilir.
// ROOT/DIRECTOR yonetim icin tum yetkili ekiplerin cirolarini aktarabilir.
const ALLOWED_ROLES = new Set(['ROOT', 'DIRECTOR', 'TEAM_MANAGER']);

const QUARTERS = [1, 2, 3, 4];

const CIRO_KANALI_LABEL = {
  SATIS_CIROSU: 'Satış Cirosu',
  PROJE_YAZIM_CIROSU: 'Proje Yazım Cirosu',
  ONAY_SONRASI_CIROSU: 'Onay Sonrası Cirosu',
};

const CiroAktarPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const canSeePage = ALLOWED_ROLES.has(user?.role);

  // Tum GraphQL akisi useCiroAktar hook'unda: query, mutation, notification,
  // role-based scope. Spec: sadece aktarim olmus ciroları getirir
  // (transfer_logs >= 1). Yil filtresi DataTable filter slot'una eklendi.
  const { entries, isLoading: entriesLoading, handleTransfer } = useCiroAktar();

  // bonus_revenue_types.coefficient -> hedefe esas ciro hesabi icin.
  const { bonusRevenueTypes, fetchData: fetchBonusRevenueTypes } = useBonusRevenueTypes();

  // Aktar modal'i state'i. row + secilen yeni ceyrek.
  const [transferRow, setTransferRow] = useState(null);
  const [transferTargetQuarter, setTransferTargetQuarter] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferSaving, setTransferSaving] = useState(false);

  useEffect(() => {
    if (user?.id) fetchBonusRevenueTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const coefficientById = useMemo(() => {
    const map = new Map();
    (bonusRevenueTypes || []).forEach((b) => {
      const c = Number(b?.coefficient);
      map.set(b.id, Number.isFinite(c) ? c : 1);
    });
    return map;
  }, [bonusRevenueTypes]);

  const handleOpenTransfer = useCallback((row) => {
    setTransferRow(row);
    // Default target: mevcut ceyregin BIR ileri ceyregi (yil siniri icinde),
    // yoksa Q1.
    const nextQ = row?.ceyrek && row.ceyrek < 4 ? row.ceyrek + 1 : 1;
    setTransferTargetQuarter(nextQ);
    setTransferNote('');
  }, []);

  const handleCloseTransfer = () => {
    if (transferSaving) return;
    setTransferRow(null);
    setTransferTargetQuarter('');
    setTransferNote('');
  };

  const handleConfirmTransfer = async () => {
    if (!transferRow) return;
    setTransferSaving(true);
    const ok = await handleTransfer(transferRow, transferTargetQuarter, transferNote);
    setTransferSaving(false);
    if (ok) {
      setTransferRow(null);
      setTransferTargetQuarter('');
      setTransferNote('');
    }
  };

  // Yil filter options'lari entries'ten turetilir — sadece sayfada gorunen
  // yillar listelenir (CiroTalepleri/teams ile ayni pattern). Hic kayit
  // yoksa filter gizlenir.
  const yearOptions = useMemo(() => {
    const yearSet = new Set();
    (entries || []).forEach((row) => {
      if (row?.yil != null) yearSet.add(Number(row.yil));
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [entries]);

  // DataTable icin enriched data. user/firm/job alanlari render'dan
  // sade tutmak ve search/filter alanlari uzerinden eslemek icin top-level
  // alanlar haline getiriliyor. __ prefix konvansiyonu birakildi —
  // camelCase alan adlari (personName, firmName, ...) kullaniyoruz.
  const enrichedRows = useMemo(() => {
    return (entries || []).map((row) => {
      const coef = coefficientById.get(row.bonus_revenue_type_id) ?? 1;
      const targetRevenue = Number(row.tutar || 0) * coef;
      const personName = row.user
        ? `${row.user.first_name || ''} ${row.user.last_name || ''}`.trim()
        : `#${row.user_id}`;
      const firmName =
        row.firm?.firm_short_name ||
        row.firm?.firm_name ||
        (row.firm_id ? `Firma #${row.firm_id}` : '-');
      const jobLabel = row.job
        ? row.job.job_title || row.job.project_number || `İş #${String(row.job.id).slice(0, 8)}`
        : '-';
      return {
        ...row,
        userTeamId: row.user?.team_id ?? null,
        personName,
        firmName,
        jobLabel,
        channelLabel: CIRO_KANALI_LABEL[row.ciro_kanali] || row.ciro_kanali,
        targetRevenue,
      };
    });
  }, [entries, coefficientById]);

  // Kolonlar merkezi TABLE_COLUMNS.ciroAktar'dan; enriched data page
  // tarafindan hazirlanir.
  const columns = TABLE_COLUMNS.ciroAktar || [];

  // CiroTalepleri pattern'i: filter dropdown options'lari datadan turetilir
  // (sadece sayfada gorunen kayitlarin ait oldugu ekipler). Hicbir satira
  // denk gelmeyen "olu" option kalmaz; ayri bir useTeams query'sine gerek
  // yok.
  const teams = useMemo(() => {
    const teamMap = new Map();
    (enrichedRows || []).forEach((row) => {
      const teamId = row?.user?.team_id;
      const teamName = row?.user?.team?.name;
      if (teamId != null && teamName) teamMap.set(teamId, teamName);
    });
    return Array.from(teamMap.entries()).map(([id, name]) => ({ id, name }));
  }, [enrichedRows]);

  // Filtre konfigleri: yil + ekip. Yil options sayfa-derived entries'ten
  // (sadece aktarim olmus yillar listelenir). Ekip options data-derived;
  // ekip filtresi sadece ROOT'a gosterilir.
  const filterConfigs = useMemo(() => {
    const base = FILTER_CONFIGS.ciroAktar || [];
    const teamOptions = teams.map((tm) => ({ value: tm.id, label: tm.name }));
    const yearOpts = yearOptions.map((y) => ({ value: y, label: String(y) }));
    return base
      .map((cfg) => {
        if (cfg.field === 'yil') return { ...cfg, options: yearOpts };
        if (cfg.field === 'userTeamId') return { ...cfg, options: teamOptions };
        return cfg;
      })
      .filter((cfg) => {
        // Yil bos ise gizle (hicbir aktarim yok); userTeamId sadece ROOT'a.
        if (cfg.field === 'yil') return yearOpts.length > 0;
        if (cfg.field === 'userTeamId') return user?.role === 'ROOT' && teamOptions.length > 0;
        return true;
      });
  }, [teams, yearOptions, user?.role]);

  // Row actions — aktar butonu satir bazinda.
  const rowActions = useCallback(
    () => [
      {
        type: 'transfer',
        label: 'ciroAktar.columns.action',
        color: 'primary',
        icon: <SwapHorizIcon fontSize="small" />,
      },
    ],
    []
  );

  const handleRowAction = useCallback(
    (actionType, row) => {
      if (actionType === 'transfer' && row) {
        // Gecmis yil cirolari aktarima kapali — yalnizca audit amacli listelenir.
        // Mevcut yilda ise daha once aktarilmis olsa bile tekrar aktarilabilir
        // (kullanici Q1→Q2→Q3 gibi zincirleme aktarim yapabilir).
        const currentYear = new Date().getFullYear();
        if (Number(row.yil) < currentYear) {
          showNotification(
            t(
              'ciroAktar.errors.pastYear',
              'Geçmiş yıl cirolarına aktarım yapılamaz.',
            ),
            'warning',
          );
          return true;
        }
        handleOpenTransfer(row);
        return true;
      }
      return false;
    },
    [handleOpenTransfer, showNotification, t]
  );

  // Yetkisiz: sayfaya ulastiysa bos ekran goster. Route disinda
  // navigation menusu/card'lari bu kosulu zaten gizliyor.
  if (!canSeePage) {
    return (
      <PageContainer currentPage={t('ciroAktar.title', 'Ciro Aktar')}>
        <Alert severity="warning" sx={{ m: 3 }}>
          {t('ciroAktar.noAccess', 'Bu sayfaya erişim yetkiniz bulunmamaktadır.')}
        </Alert>
      </PageContainer>
    );
  }

  const transferLogs = transferRow?.transfer_logs || [];

  return (
    <>
      <PageContainer currentPage={t('ciroAktar.title', 'Ciro Aktar')}>
        <DataTableContainer
          title={t('ciroAktar.title', 'Ciro Aktar')}
          entityType="ciroAktar"
          data={enrichedRows}
          loading={entriesLoading}
          columns={columns}
          filterConfigs={filterConfigs}
          searchFields={SEARCH_CONFIGS.ciroAktar?.fields || []}
          searchPlaceholder={t('ciroAktar.search.placeholder', 'Ciro kaydı ara...')}
          showAddButton={false}
          exportButton={false}
          rowActions={rowActions}
          onRowAction={handleRowAction}
        />
      </PageContainer>

      <Dialog
        open={Boolean(transferRow)}
        onClose={handleCloseTransfer}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('ciroAktar.modal.title', 'Bu cironun tamamını hangi çeyreğe aktarmak istersin?')}
        </DialogTitle>
        <DialogContent dividers>
          {transferRow && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('ciroAktar.modal.currentQuarter', 'Mevcut çeyrek')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {transferRow.yil} / Q{transferRow.ceyrek}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('ciroAktar.modal.transferredBy', 'Aktaran')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || '-'}
                  </Typography>
                </Box>
              </Box>
              <FormControl fullWidth size="small">
                <InputLabel id="target-quarter-label">
                  {t('ciroAktar.modal.targetQuarter', 'Hedef Çeyrek')}
                </InputLabel>
                <Select
                  labelId="target-quarter-label"
                  label={t('ciroAktar.modal.targetQuarter', 'Hedef Çeyrek')}
                  value={transferTargetQuarter}
                  onChange={(e) => setTransferTargetQuarter(e.target.value)}
                >
                  {QUARTERS.filter((q) => q !== transferRow.ceyrek).map((q) => (
                    <MenuItem key={q} value={q}>Q{q}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label={t('ciroAktar.modal.note', 'Not (opsiyonel)')}
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
              />
              <Typography variant="caption" color="text.secondary">
                {t(
                  'ciroAktar.modal.warning',
                  'Cironun tamamı seçtiğiniz çeyreğe taşınır. Kısmi aktarım yapılamaz.',
                )}
              </Typography>

              {/* Audit history: bu kaydin onceki tum aktarimlari.
                  Bos ise hicbir sey gostermiyoruz — gereksiz UI gurultusu. */}
              {transferLogs.length > 0 && (
                <Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <HistoryIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {t('ciroAktar.modal.history', 'Aktarım Geçmişi')}
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    {transferLogs.map((log) => {
                      const personLabel = log?.transferred_by_user
                        ? `${log.transferred_by_user.first_name || ''} ${log.transferred_by_user.last_name || ''}`.trim()
                        : '-';
                      return (
                        <Box
                          key={log.id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.25,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {`Q${log.from_quarter} → Q${log.to_quarter}`} · {personLabel}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.transferred_at ? formatDateTR(log.transferred_at) : ''}
                          </Typography>
                          {log.note && (
                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                              “{log.note}”
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransfer} disabled={transferSaving}>
            {t('ciroAktar.modal.cancel', 'Vazgeç')}
          </Button>
          <Button
            onClick={handleConfirmTransfer}
            variant="contained"
            color="primary"
            disabled={transferSaving || !transferTargetQuarter}
            startIcon={transferSaving ? <CircularProgress size={16} /> : <SwapHorizIcon />}
          >
            {t('ciroAktar.modal.confirm', 'Aktar')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CiroAktarPage;
